import { InfoIcon } from "@carbon6/c6-frontend-design/icons";
import { IconButton, Modal, Scrollbar, Text, TextInput } from "@carbon6/c6-frontend-design";
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import {
  DATASET_FILE_PROBE_MAX,
  fetchAllPages,
  type DatasetFileKind,
  type DirectoryTree,
} from "@/api";
import { storagePutContract, type DatasetStorageBackend } from "@/api/datasetStorageBackend";
import { hatcheryTextModes } from "@/app/theme";
import { FolderOutlineIcon, UploadIcon } from "@/components/icons";
import { HatModalHeader, HatTextModal } from "@/components/modal";
import { createLogger } from "@/logging";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatBytes, formatNumber } from "@/utils";
import {
  extraFileRelativePath,
  mergePickedFiles,
  uploadExtraFilesToFolder,
  type ExtraFileUploadResult,
} from "../extraFileUpload";
import type { FileBrowserSource } from "../fileBrowserSource";
import { resolveLoadErrorMessage, type LoadState } from "../loadState";
import { storageBackendLabel } from "../storageBackendText";
import {
  buildNewFolderPath,
  formatUploadTarget,
  uploadTargetOptions,
  type UploadTargetOption,
} from "./uploadTarget";
import styles from "./ExtraFileUploadDialog.module.css";

const logger = createLogger("components/dataset/fileBrowser/upload");

export type ExtraFileUploadDialogProps = {
  source: FileBrowserSource;
  datasetUuid: string;
  // 이 Dataset 의 저장 위치. 위치마다 파일 크기 상한과 "이미 있는 경로"의 거절이 다르다. 모르면
  // 크기를 미리 막지 않는다.
  storageBackend?: DatasetStorageBackend;
  // 다이얼로그를 연 자리의 폴더(사이드바=루트, 폴더 행=그 폴더). 초기 대상으로 쓴다.
  initialPath: string;
  onClose: () => void;
  // 한 건 이상 업로드되면 호출한다. 부모는 트리와 목록을 다시 조회한다.
  onUploaded: () => void;
};

/**
 * 추가 파일 업로드 다이얼로그. 좌측에서 대상 폴더를 고르고 우측에 파일을 놓는다.
 *
 * 파일 선택창을 바로 띄우지 않고 **대상 경로를 먼저 정하게** 한다. 디렉터리 트리를 그대로 보여
 * 주므로 사용자는 업로드 위치를 확인할 수 있다. 없는 폴더는 여기서 새로 만든다.
 *
 * **새 폴더는 이 업로드로만 생긴다.** 서버는 폴더를 따로 저장하지 않고 파일 경로에서 트리를
 * 집계한다. 그래서 파일을 올리지 않고 닫으면 그 폴더는 남지 않는다.
 *
 * **같은 이름은 업로드 전에 확인한다.** 고른 이름을 대상 폴더에 미리 조회한다(`filename` probe).
 * extra 파일과 겹치면 덮어쓴다고 표시하고 확인을 한 번 받는다. Dataset 파일(episode·meta)과
 * 겹치면 그 줄을 막는다. 서버도 409 로 거부하지만, 다이얼로그가 문제 파일을 먼저 표시한다.
 *
 * Owns: 대상 선택 상태 · 파일 선택(버튼·드롭) · 같은 이름 probe · 업로드 실행과 그 오류 표시.
 * Does not own: 대상 후보 계산·경로 검증(`uploadTarget`) · 선택 병합·전송 절차(`extraFileUpload`).
 */
export function ExtraFileUploadDialog({
  source,
  datasetUuid,
  storageBackend,
  initialPath,
  onClose,
  onUploaded,
}: ExtraFileUploadDialogProps): ReactNode {
  const [tree, setTree] = useState<LoadState<DirectoryTree>>({ status: "loading" });
  const [targetPath, setTargetPath] = useState(initialPath);
  // 이 다이얼로그에서 만든 새 폴더. 서버 트리에 없으므로 목록에 직접 추가해야 한다.
  const [pendingFolder, setPendingFolder] = useState<string | null>(null);
  // 새 폴더 이름 입력은 버튼을 눌러야 열린다(null=닫힘). 평소에는 입력이 폴더 목록 공간을 차지하지 않는다.
  const [folderInput, setFolderInput] = useState<string | null>(null);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [files, setFiles] = useState<readonly File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // 대상 폴더에 이미 있는 같은 이름 → 그 파일의 카탈로그. extra 면 덮어쓰고, Dataset 파일이면 막는다.
  const [existing, setExisting] = useState<ReadonlyMap<string, DatasetFileKind>>(new Map());
  const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // 대상 후보는 트리에서 가져온다. 다이얼로그를 열 때 한 번 조회하고, 업로드 중에는 다시 조회하지 않는다.
  // 조회는 브라우저와 같은 seam(source.directoryTree)을 쓴다.
  useEffect(() => {
    let cancelled = false;
    setTree({ status: "loading" });
    void source
      .directoryTree(datasetUuid)
      .then((data) => {
        if (!cancelled) setTree({ status: "success", data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("directory tree (upload dialog) failed", error);
        setTree({ status: "error", message: resolveLoadErrorMessage(error) });
      });
    return () => {
      cancelled = true;
    };
    // source 는 모듈 상수(안정)다. datasetUuid 가 실제 키다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetUuid]);

  // 고른 이름이 대상 폴더에 이미 있는지 서버에 조회한다. 새 폴더는 서버에 없으므로 조회하지 않는다.
  // 조회가 실패하면 결과를 조용히 비운다. 최종 판정은 서버(409)가 하므로 여기서 업로드를 막지 않는다.
  useEffect(() => {
    if (files.length === 0 || targetPath === pendingFolder) {
      setExisting(new Map());
      return;
    }
    let cancelled = false;
    void probeExistingNames(
      source,
      datasetUuid,
      targetPath,
      files.map((file) => file.name),
    )
      .then((found) => {
        if (!cancelled) setExisting(found);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.warn("existing name probe failed", error);
        setExisting(new Map());
      });
    return () => {
      cancelled = true;
    };
    // source 는 모듈 상수(안정)다. 대상 폴더와 파일 목록이 실제 키다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetUuid, targetPath, pendingFolder, files]);

  // 입력을 열면 바로 타이핑할 수 있게 한다. 버튼을 누른 사용자는 곧 이름을 입력한다.
  useEffect(() => {
    if (folderInput !== null) {
      folderInputRef.current?.focus();
    }
    // 값이 바뀔 때마다 포커스를 가져가지 않도록 "열림 여부"만 확인한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderInput !== null]);

  const options = uploadTargetOptions(tree.status === "success" ? tree.data : null, pendingFolder);
  const overwriteCount = files.filter((file) => existing.get(file.name) === "extra").length;
  const blockedCount = files.filter((file) => isDatasetFile(existing.get(file.name))).length;
  // 저장 위치가 PUT 한 번으로 받지 못하는 크기의 파일. 올려도 storage 가 거절하므로 미리 막는다.
  const putContract = storagePutContract(storageBackend);
  const maxFileBytes = putContract.maxFileBytes;
  const oversizeCount =
    maxFileBytes === null ? 0 : files.filter((file) => file.size > maxFileBytes).length;

  function confirmFolder(): void {
    const result = buildNewFolderPath(targetPath, folderInput ?? "");
    if (!result.ok) {
      setFolderError(result.reason);
      return;
    }
    setPendingFolder(result.path);
    setTargetPath(result.path);
    setFolderInput(null);
    setFolderError(null);
  }

  function cancelFolder(): void {
    setFolderInput(null);
    setFolderError(null);
  }

  // Enter=확인 / Esc=취소. 두 키가 모달 기본 동작(제출·닫기)을 실행하지 않게 막는다.
  function handleFolderKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      event.preventDefault();
      confirmFolder();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      cancelFolder();
    }
  }

  function addFiles(incoming: readonly File[]): void {
    if (incoming.length === 0) return;
    setFiles((current) => mergePickedFiles(current, incoming));
    setUploadError(null);
  }

  function pickFiles(event: ChangeEvent<HTMLInputElement>): void {
    // FileList는 input 초기화와 함께 비워지므로 먼저 복사한다.
    const picked = Array.from(event.target.files ?? []);
    event.target.value = ""; // 같은 파일 재선택 허용.
    addFiles(picked);
  }

  async function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    if (uploading || files.length === 0 || blockedCount > 0 || oversizeCount > 0) return;
    // 덮어쓰기는 한 번 더 확인한다. 같은 이름의 extra 파일이 새 내용으로 바뀌고, 되돌릴 수 없다.
    if (overwriteCount > 0) {
      setOverwriteConfirmOpen(true);
      return;
    }
    await runUpload();
  }

  async function runUpload(): Promise<void> {
    setOverwriteConfirmOpen(false);
    setUploading(true);
    setUploadError(null);
    try {
      const result = await uploadExtraFilesToFolder(
        source,
        datasetUuid,
        targetPath || undefined,
        files,
        storageBackend,
      );
      if (result.uploaded > 0) onUploaded();
      if (result.failed === 0) {
        onClose();
        return;
      }
      // 일부만 실패하면 닫지 않는다. 사용자는 남은 파일을 보고 다시 시도할 수 있어야 한다.
      setUploadError(uploadFailureText(result, putContract.existsStatusAmbiguous));
    } catch (error) {
      logger.error("upload extra files failed", error);
      setUploadError(resolveLoadErrorMessage(error));
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      closeOnBackdropClick={!uploading}
      closeOnEscape={!uploading}
      aria-labelledby="extra-file-upload-title"
    >
      <HatModalHeader
        title="추가 파일을 업로드합니다."
        titleId="extra-file-upload-title"
        close={{ disabled: uploading }}
      />
      <form className={styles.form} onSubmit={(event) => void submit(event)}>
        {/* 왼쪽은 대상 폴더, 오른쪽은 파일이다. 폴더 목록이 다이얼로그를 모두 차지하지 않게 반씩 나눈다. */}
        <div className={styles.body}>
          <section className={styles.column} aria-label="대상 폴더">
            {/* 열 제목은 두지 않는다. 폴더 목록과 드롭 영역은 모양으로 이미 구분된다.
                새 폴더는 폴더 목록의 동작이므로 그 열의 오른쪽 끝에 둔다. */}
            <div className={styles.columnHead}>
              <IconButton
                text="새 폴더"
                type="button"
                size="sm"
                variant="surface"
                disabled={uploading || folderInput !== null}
                onClick={() => setFolderInput("")}
              />
            </div>
            <Scrollbar className={styles.tree} aria-label="대상 폴더 목록">
              {tree.status === "loading" ? (
                <LoadingState size="sm" mode="plain">
                  폴더를 불러오고 있습니다.
                </LoadingState>
              ) : null}
              {tree.status === "error" ? (
                <EmptyState size="sm" mode="plain" tone="error">
                  {tree.message}
                </EmptyState>
              ) : null}
              {folderInput !== null ? (
                <div className={styles.newFolderRow}>
                  <TextInput
                    ref={folderInputRef}
                    value={folderInput}
                    maxLength={255}
                    placeholder={`"${formatUploadTarget(targetPath)}" 아래 새 폴더 이름`}
                    aria-label="새 폴더 이름"
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setFolderInput(event.target.value);
                      setFolderError(null);
                    }}
                    onKeyDown={handleFolderKeyDown}
                  />
                  <IconButton
                    text="확인"
                    type="button"
                    size="sm"
                    variant="solid"
                    disabled={folderInput.trim() === ""}
                    onClick={confirmFolder}
                  />
                  <IconButton
                    text="취소"
                    type="button"
                    size="sm"
                    variant="surface"
                    aria-label="새 폴더 취소"
                    onClick={cancelFolder}
                  />
                </div>
              ) : null}
              {options.map((option) => (
                <TargetRow
                  key={option.path === "" ? "__root" : option.path}
                  option={option}
                  selected={option.path === targetPath}
                  disabled={uploading}
                  onSelect={() => {
                    setTargetPath(option.path);
                    setFolderError(null);
                  }}
                />
              ))}
            </Scrollbar>
            {folderError ? (
              <Text
                variant={hatcheryTextModes["body-normal"]}
                className={styles.error}
                role="alert"
              >
                {folderError}
              </Text>
            ) : null}
            {pendingFolder !== null ? (
              <Text variant={hatcheryTextModes["caption-normal"]} className={styles.muted}>
                새 폴더 &quot;{pendingFolder}&quot; 는 파일을 올려야 실제로 만들어집니다.
              </Text>
            ) : null}
          </section>

          <section className={styles.column} aria-label="업로드할 파일">
            <div className={styles.columnHead}>
              {files.length > 0 ? (
                <Text variant={hatcheryTextModes["caption-normal"]} className={styles.muted}>
                  {files.length}개 · {formatBytes(totalBytes(files))}
                </Text>
              ) : null}
            </div>
            <FileDropZone
              files={files}
              targetPath={targetPath}
              existing={existing}
              maxFileBytes={maxFileBytes}
              disabled={uploading}
              onAdd={addFiles}
              onRemove={(name) =>
                setFiles((current) => current.filter((file) => file.name !== name))
              }
              onBrowse={() => fileInputRef.current?.click()}
            />
          </section>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          aria-label="업로드할 파일 선택"
          onChange={pickFiles}
        />

        <aside className={styles.notice} aria-label="Annotation 경로 안내">
          <InfoIcon aria-hidden="true" />
          <div>
            <p>
              <code>annotation/random_background/</code>와 등록된 Annotation Set 경로는 직접 수정할
              수 없습니다.
            </p>
            <p>
              새 번들은 <strong>Dataset 상세 → Annotation → 파일에서 가져오기</strong>를 이용해
              주세요. Annotation Set에 연결되지 않은 기존 <code>annotation_sets/</code> 파일은
              여기서 관리할 수 있습니다.
            </p>
          </div>
        </aside>

        {uploadError ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            {uploadError}
          </Text>
        ) : null}
        {blockedCount > 0 ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            Dataset 파일과 이름이 같은 파일이 있습니다. 목록에서 빼 주세요.
          </Text>
        ) : null}
        {oversizeCount > 0 && storageBackend !== undefined && maxFileBytes !== null ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            {oversizeText(storageBackend, maxFileBytes)} 더 큰 파일은 목록에서 빼 주세요.
          </Text>
        ) : null}

        <div className={styles.actions}>
          <IconButton
            text="취소"
            type="button"
            variant="surface"
            disabled={uploading}
            onClick={onClose}
          />
          <IconButton
            text={submitLabel(uploading, overwriteCount)}
            type="submit"
            variant="solid"
            tone="primary"
            disabled={uploading || files.length === 0 || blockedCount > 0 || oversizeCount > 0}
          />
        </div>
      </form>

      <HatTextModal
        open={overwriteConfirmOpen}
        label={`추가 파일 ${overwriteCount}개를 덮어씁니다.`}
        caption="같은 이름의 추가 파일이 새 내용으로 바뀝니다. 이전 내용은 되돌릴 수 없습니다."
        error
        okText="덮어쓰고 업로드"
        busy={uploading}
        onOkPress={() => void runUpload()}
        onCancelPress={() => {
          if (!uploading) setOverwriteConfirmOpen(false);
        }}
      />
    </Modal>
  );
}

// Dataset 파일(episode·meta)과 이름이 겹치면 그 줄은 올릴 수 없다. 원본은 절대 덮어쓰지 않는다.
function isDatasetFile(kind: DatasetFileKind | undefined): boolean {
  return kind === "episode" || kind === "meta";
}

// 저장 위치의 파일 크기 상한을 알리는 문장. 목록의 크기와 같은 단위로 적고, 정확한 값을 괄호에 둔다.
// 예) "Azure에 저장된 Dataset은 파일 하나를 4.9 GB(5,000 MiB)까지 받습니다."
function oversizeText(storageBackend: DatasetStorageBackend, maxFileBytes: number): string {
  const mebibytes = formatNumber(maxFileBytes / (1024 * 1024));
  return `${storageBackendLabel(storageBackend)}에 저장된 Dataset은 파일 하나를 ${formatBytes(maxFileBytes)}(${mebibytes} MiB)까지 받습니다.`;
}

/**
 * 일부 또는 전부 실패한 업로드의 안내 문구.
 *
 * "이미 있는 경로"의 status 로 거절된 파일은 사유와 다음 행동을 따로 알린다. 업로드 전의 이름 조회는
 * 카탈로그만 본다. 카탈로그가 모르는 object 와 동시 업로드는 storage 가 PUT 에서 거절한다.
 *
 * 그 status 가 만료된 서명과 겹치는 저장 위치(`ambiguous`)에서는 사유를 단정하지 않는다. 다시 시도를
 * 먼저 안내한다. 만료라면 다시 시도로 풀리고, 이미 있는 경로라면 같은 결과가 나온다.
 */
function uploadFailureText(
  { uploaded, failed, alreadyExists }: ExtraFileUploadResult,
  ambiguous: boolean,
): string {
  const outcome =
    uploaded > 0
      ? `${uploaded}개를 올렸고 ${failed}개는 올리지 못했습니다.`
      : "파일을 업로드하지 못했습니다.";
  if (alreadyExists === 0) {
    return uploaded > 0
      ? `${outcome} 남은 파일을 다시 선택해 주세요.`
      : `${outcome} 잠시 후 다시 시도해 주세요.`;
  }
  const cause = ambiguous
    ? "같은 경로에 파일이 이미 있거나 업로드 링크가 만료됐습니다."
    : "같은 경로에 파일이 이미 있습니다.";
  const reason = alreadyExists === failed ? cause : `그중 ${alreadyExists}개는 ${cause}`;
  const action = ambiguous
    ? "다시 시도해도 같으면 이름을 바꿔 올려 주세요."
    : "이름을 바꿔 올리거나 파일 목록을 새로 고쳐 확인해 주세요.";
  return `${outcome} ${reason} ${action}`;
}

function submitLabel(uploading: boolean, overwriteCount: number): string {
  if (uploading) return "업로드 중…";
  return overwriteCount > 0 ? `${overwriteCount}개 덮어쓰고 업로드` : "업로드";
}

// 대상 폴더에서 고른 이름들을 찾는다. 서버 probe 상한 단위로 나누어 조회하고, 이름 → 카탈로그로 모은다.
async function probeExistingNames(
  source: FileBrowserSource,
  datasetUuid: string,
  parent: string,
  names: readonly string[],
): Promise<Map<string, DatasetFileKind>> {
  const found = new Map<string, DatasetFileKind>();
  for (let start = 0; start < names.length; start += DATASET_FILE_PROBE_MAX) {
    const chunk = names.slice(start, start + DATASET_FILE_PROBE_MAX);
    const rows = await fetchAllPages((query) =>
      source.folderFiles(datasetUuid, { ...query, parent, filename: chunk }),
    );
    for (const row of rows) {
      found.set(row.filename, row.kind);
    }
  }
  return found;
}

function totalBytes(files: readonly File[]): number {
  return files.reduce((sum, file) => sum + file.size, 0);
}

/**
 * 파일을 놓거나 골라 추가하는 영역. 비어 있으면 안내를, 파일이 있으면 **올라갈 최종 경로** 목록을 보여 준다.
 *
 * 드롭과 선택 버튼이 같은 영역을 쓴다. 목록에 파일이 있어도 파일을 계속 놓을 수 있어야 한다.
 * 그래서 목록 전체가 드롭 대상이다.
 */
function FileDropZone({
  files,
  targetPath,
  existing,
  maxFileBytes,
  disabled,
  onAdd,
  onRemove,
  onBrowse,
}: {
  files: readonly File[];
  targetPath: string;
  // 대상 폴더에 이미 있는 같은 이름 → 카탈로그. 줄마다 덮어씀·원본 표시의 근거다.
  existing: ReadonlyMap<string, DatasetFileKind>;
  // 파일 하나의 크기 상한(바이트). 넘는 줄은 올릴 수 없다고 표시한다. null 이면 상한이 없다.
  maxFileBytes: number | null;
  disabled: boolean;
  onAdd: (files: readonly File[]) => void;
  onRemove: (name: string) => void;
  onBrowse: () => void;
}): ReactNode {
  const [dragging, setDragging] = useState(false);
  // dragenter/leave 는 자식 위를 지날 때마다 발생한다. 깊이를 세야 테두리가 깜박이지 않는다.
  const depth = useRef(0);

  function reset(): void {
    depth.current = 0;
    setDragging(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    reset();
    if (disabled) return;
    const dropped = event.dataTransfer?.files;
    onAdd(dropped === undefined || dropped === null ? [] : Array.from(dropped));
  }

  return (
    <div
      className={styles.dropZone}
      // 드롭을 받는 요소는 이 영역이다. 바깥 열이 아니라 이 영역에 이름을 붙여 대상을 분명히 한다.
      role="group"
      aria-label="파일 놓기 영역"
      data-dragging={dragging ? "true" : undefined}
      data-empty={files.length === 0 ? "true" : undefined}
      onDragEnter={(event) => {
        event.preventDefault();
        depth.current += 1;
        if (!disabled) setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={() => {
        depth.current -= 1;
        if (depth.current <= 0) reset();
      }}
      onDrop={handleDrop}
    >
      {files.length === 0 ? (
        <div className={styles.dropEmpty}>
          <span className={styles.dropIcon} aria-hidden="true">
            <UploadIcon />
          </span>
          <Text variant={hatcheryTextModes["body-bold"]}>여기로 파일을 끌어다 놓으세요</Text>
          <IconButton
            text="파일 선택"
            type="button"
            size="md"
            variant="surface"
            disabled={disabled}
            onClick={onBrowse}
          />
        </div>
      ) : (
        <>
          <Scrollbar className={styles.fileList} aria-label="업로드할 파일 목록">
            {files.map((file) => (
              <div
                key={file.name}
                className={styles.fileRow}
                data-blocked={
                  isDatasetFile(existing.get(file.name)) ||
                  (maxFileBytes !== null && file.size > maxFileBytes)
                    ? "true"
                    : undefined
                }
              >
                <span className={styles.filePath}>
                  {extraFileRelativePath(targetPath || undefined, file.name)}
                </span>
                {existing.get(file.name) === "extra" ? (
                  <span className={styles.overwrite} title="같은 이름의 추가 파일을 덮어씁니다.">
                    덮어씀
                  </span>
                ) : null}
                {isDatasetFile(existing.get(file.name)) ? (
                  <span className={styles.blocked} title="Dataset 파일과 이름이 같습니다.">
                    원본
                  </span>
                ) : null}
                {maxFileBytes !== null && file.size > maxFileBytes ? (
                  <span className={styles.blocked} title="이 저장 위치가 받는 크기를 넘습니다.">
                    크기 초과
                  </span>
                ) : null}
                <span className={styles.fileSize}>{formatBytes(file.size)}</span>
                <IconButton
                  className={styles.remove}
                  text="빼기"
                  type="button"
                  size="sm"
                  variant="surface"
                  aria-label={`${file.name} 빼기`}
                  disabled={disabled}
                  onClick={() => onRemove(file.name)}
                />
              </div>
            ))}
          </Scrollbar>
          <div className={styles.dropFooter}>
            <Text variant={hatcheryTextModes["caption-normal"]} className={styles.muted}>
              더 끌어다 놓거나
            </Text>
            <IconButton
              text="파일 추가"
              type="button"
              size="sm"
              variant="surface"
              disabled={disabled}
              onClick={onBrowse}
            />
          </div>
        </>
      )}
    </div>
  );
}

function TargetRow({
  option,
  selected,
  disabled,
  onSelect,
}: {
  option: UploadTargetOption;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={styles.targetRow}
      style={{ paddingLeft: `calc(${option.depth} * 0.85rem + var(--app-spacing-xsmall))` }}
      data-selected={selected ? "true" : undefined}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
    >
      <span className={styles.targetIcon} aria-hidden="true">
        <FolderOutlineIcon />
      </span>
      <span className={styles.targetName}>{option.name}</span>
      {option.pending ? <span className={styles.badge}>새 폴더</span> : null}
    </button>
  );
}
